import { Option } from '@sapphire/result';
import { match } from 'ts-pattern';
import {
  classifySurvey,
  DeprecatedQualificationRecord,
  deprecatedQualificationAcrossRecordsWarning,
  deprecatedQualificationWarning,
  mapSurveyLocalizationFor,
  Survey,
  SURVEY_CLASSIFICATION_KIND,
  SurveyKind,
} from '@ps/survey-utils';

import type { SurveyChildKey } from '../../survey-creation-shared-state.model';
import type { SurveyChildRegistryState } from '../../survey-creation-state.model';
import {
  isLoadedCoreSibling,
  registeredSurveys,
} from '../../core/utils/core-children.helper';

export function surveysByLocale(
  surveys: readonly Survey[],
): Map<string, readonly Survey[]> {
  const groups = new Map<string, Survey[]>();
  for (const survey of surveys) {
    groups.set(mapSurveyLocalizationFor(survey), [
      ...(groups.get(mapSurveyLocalizationFor(survey)) ?? []),
      survey,
    ]);
  }
  return groups;
}

// A standalone survey alone; an already-loaded family from the registry; None for a family's first load.
export function rootLoadSurveys(
  survey: Survey,
  children: Record<SurveyChildKey, SurveyChildRegistryState>,
): Option<readonly Survey[]> {
  return SurveyKind.childFilter(survey).isNone()
    ? Option.some([survey])
    : Option.some<readonly Survey[]>(registeredSurveys(children)).filter(() =>
        isLoadedCoreSibling(children, survey.id),
      );
}

// One message per qualification, worded for the survey's kind.
export function deprecatedQualificationWarnings(
  surveys: readonly Survey[],
  deprecated: readonly DeprecatedQualificationRecord[],
): string[] {
  const names = new Map(
    deprecated.map((record) => [
      record.qualificationCode,
      record.qualificationName,
    ]),
  );
  return Option.from(surveys[0]).mapOr([], (survey) => {
    const warningFor = warningForKind(survey);
    return [...names.values()].map(warningFor);
  });
}

function warningForKind(
  survey: Survey,
): (_qualificationName: string) => string {
  return match(classifySurvey(survey).kind)
    .with(
      SURVEY_CLASSIFICATION_KIND.STANDARD,
      SURVEY_CLASSIFICATION_KIND.TC,
      () => (name: string) => deprecatedQualificationWarning(name),
    )
    .with(
      SURVEY_CLASSIFICATION_KIND.MC,
      SURVEY_CLASSIFICATION_KIND.GROUP_MC,
      () => (name: string) =>
        deprecatedQualificationAcrossRecordsWarning(name, 'locales'),
    )
    .with(
      SURVEY_CLASSIFICATION_KIND.TRACKER,
      () => (name: string) =>
        deprecatedQualificationAcrossRecordsWarning(name, 'waves'),
    )
    .with(
      SURVEY_CLASSIFICATION_KIND.GROUPED,
      () => (name: string) =>
        deprecatedQualificationAcrossRecordsWarning(name, 'surveys'),
    )
    .exhaustive();
}
